import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import {
  emailsGridActions,
  reducer as emailsGridReducer,
  sliceKey as emailsGridSliceKey,
} from 'app/pages/EmailsGridPage/slice';
import { uploadLeadsDetailsSaga } from 'app/pages/EmailsGridPage/saga';
import { useSegmentInventories } from 'app/pages/EmailsGridPage/hooks/useSegmentInventories';
import { saveGenerationState } from 'app/pages/EmailsGridPage/utils/generationPersistence';

export type UseEmbeddedTemplateGenerationOptions = {
  dealerId: string;
  /** When true, preferences are fetched (same as opening Preferences modal). */
  enabled: boolean;
};

/**
 * Preferences + generate actions for embedded contexts (e.g. HIL resolve modal).
 * Progress card + socket live on the parent page via `useEmailTemplateGenerationProgressHost`.
 */
export function useEmbeddedTemplateGeneration({
  dealerId,
  enabled,
}: UseEmbeddedTemplateGenerationOptions) {
  useInjectReducer({ key: emailsGridSliceKey, reducer: emailsGridReducer });
  useInjectSaga({ key: emailsGridSliceKey, saga: uploadLeadsDetailsSaga });

  const dispatch = useDispatch();
  const segmentInventories = useSegmentInventories(dealerId, enabled);

  const handleSavePreferences = React.useCallback(async () => {
    try {
      await segmentInventories.savePreferences();
      dispatch(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'success',
          message: 'Preferences saved successfully',
        }),
      );
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : '';
      if (msg) {
        dispatch(
          notificationAlertActions.addNotification({
            open: true,
            severity: 'error',
            message: msg,
          }),
        );
      }
      throw err;
    }
  }, [dispatch, segmentInventories]);

  const handleGenerate = React.useCallback(async () => {
    if (!dealerId) return;
    try {
      if (segmentInventories.canSavePreferences) {
        await segmentInventories.savePreferences();
      }
      dispatch(emailsGridActions.setGenerateProgress({ current: 0, total: 1 }));
      dispatch(
        emailsGridActions.requestGenerateEmailTemplates({ dealerId }),
      );
      saveGenerationState(dealerId, {
        generateInProgress: true,
        progress: { current: 0, total: 1 },
        socketMessages: [],
        lastSocketMessage: null,
      });
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : '';
      if (msg) {
        dispatch(
          notificationAlertActions.addNotification({
            open: true,
            severity: 'error',
            message: msg,
          }),
        );
      }
    }
  }, [dealerId, dispatch, segmentInventories]);

  return {
    segmentInventories,
    handleSavePreferences,
    handleGenerate,
    preferencesSaveLoading: segmentInventories.saveLoading,
    canSavePreferences: segmentInventories.canSavePreferences,
  };
}
